/* eslint-disable max-lines */
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { ValidationError } from 'yup';
import { BaseError } from '@utils/types/shared/base';
import { isFairUseSoftLimit } from './fair-use-retry';

/**
 * Extract a user-friendly error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  // Handle Yup ValidationError (thrown by axios interceptor for 422 errors)
  if (error instanceof ValidationError) {
    return error.message || 'Validation error. Please check your input.';
  }

  // Handle Axios errors
  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<BaseError>;

    // Check for API error description (human-readable) first
    if (axiosError.response?.data?.error_description) {
      return axiosError.response.data.error_description;
    }

    // Check for API message
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    // Fall back to the API error code
    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error;
    }

    // Handle common HTTP status codes
    switch (axiosError.response?.status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Authentication required. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return 'Validation error. Please check your input.';
      case 429:
        if (axiosError.response?.headers?.['x-fair-use-soft-limit']) {
          return 'You have hit your fair-use soft limit. Uploads will keep going, just a little slower.';
        }
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        if (axiosError.message) {
          return axiosError.message;
        }
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Determine if an error should be shown to the user
 */
export function shouldShowErrorToUser(error: unknown): boolean {
  // 422 ValidationErrors (yup-wrapped) — let the form's local onError handle it
  // with a field-specific message. The global "Validation error. Please check
  // your input." would just stack on top and obscure the useful one.
  if (error instanceof ValidationError) {
    return false;
  }

  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;

    // Same reasoning for raw axios 422s (FastAPI returns `detail` array, no
    // ValidationError wrapping). Local form handlers are the right surface.
    if (axiosError.response?.status === 422) {
      return false;
    }

    // Fair-use soft limit: our retry helper already shows a single banner toast.
    if (isFairUseSoftLimit(error)) {
      return false;
    }

    // Don't show network errors (user likely knows they're offline)
    if (axiosError.code === 'ERR_NETWORK') {
      return false;
    }

    // Don't show auth errors (handled by auth context)
    if (axiosError.response?.status === 401) {
      return false;
    }

    // Don't show timeout errors as toast (too technical)
    if (axiosError.code === 'ECONNABORTED') {
      return false;
    }
  }

  return true;
}

/**
 * Determine if an error should be logged to Sentry
 */
function shouldLogToSentry(error: unknown): boolean {
  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<BaseError>;
    const status = axiosError.response?.status;

    // Don't log 400 errors - validation errors handled by forms
    // Examples: invalid email, password requirements not met, expired activation links
    if (status === 400) {
      return false;
    }

    // Don't log 401 errors - handled by auth context
    if (status === 401) {
      return false;
    }

    // Don't log 403 errors - permission denied, handled by application
    // Examples: users without group access, users accessing admin-only endpoints, etc.
    if (status === 403) {
      return false;
    }

    // Don't log 404 errors - they're handled by the UI
    if (status === 404) {
      return false;
    }

    // Don't log 422 errors - validation errors handled by forms
    // Examples: invalid email format, password requirements not met, etc.
    if (status === 422) {
      return false;
    }

    // Don't log network errors (user offline, etc.)
    if (axiosError.code === 'ERR_NETWORK') {
      return false;
    }
  }

  return true;
}

/**
 * Log error to Sentry with additional context
 */
export function logErrorToSentry(
  error: unknown,
  context: {
    hookName?: string;
    endpoint?: string;
    method?: string;
    variables?: unknown;
  } = {}
) {
  // Skip logging if this is an expected error
  if (!shouldLogToSentry(error)) {
    return;
  }

  const { hookName, endpoint, method, variables } = context;

  // Add breadcrumb for React Query context
  if (hookName) {
    const breadcrumbData: Record<string, string> = {
      hookName,
      ...(endpoint && { endpoint }),
      ...(method && { method })
    };

    if (variables) {
      try {
        breadcrumbData.variables = JSON.stringify(variables);
      } catch {
        breadcrumbData.variables = '[Unable to stringify]';
      }
    }

    Sentry.addBreadcrumb({
      category: 'react-query',
      message: `Error in ${hookName}`,
      level: 'error',
      data: breadcrumbData
    });
  }

  // Set additional context
  if (endpoint || method) {
    Sentry.setContext('api-call', {
      endpoint,
      method,
      hookName
    });
  }

  // Extract error details for Sentry
  let errorToLog: Error;

  if (error instanceof Error) {
    errorToLog = error;
  } else if (typeof error === 'string') {
    errorToLog = new Error(error);
  } else {
    errorToLog = new Error('Unknown error occurred');
  }

  // Add ValidationError-specific context
  if (error instanceof ValidationError) {
    Sentry.setContext('validation-error', {
      type: 'ValidationError',
      message: error.message,
      value: error.value,
      path: error.path,
      errors: error.errors
    });
  }

  // Add Axios-specific context if available
  if ('isAxiosError' in errorToLog) {
    const axiosError = errorToLog as AxiosError<BaseError>;
    Sentry.setContext('axios-error', {
      status: axiosError.response?.status,
      statusText: axiosError.response?.statusText,
      url: axiosError.config?.url,
      method: axiosError.config?.method,
      data: axiosError.response?.data,
      code: axiosError.code
    });
  }

  // Capture the exception
  Sentry.captureException(errorToLog);
}

/**
 * Global error handler for React Query mutations
 */
export function handleMutationError(
  error: unknown,
  options: {
    hookName?: string;
    endpoint?: string;
    method?: string;
    variables?: unknown;
    showToast?: boolean;
    customMessage?: string;
  } = {}
) {
  const {
    hookName,
    endpoint,
    method,
    variables,
    showToast = true,
    customMessage
  } = options;

  // Log to Sentry with context
  logErrorToSentry(error, { hookName, endpoint, method, variables });

  // Show user-friendly error message if appropriate
  if (showToast && shouldShowErrorToUser(error)) {
    const message = customMessage || getErrorMessage(error);
    toast.error(message);
  }
}

/**
 * Global error handler for React Query queries
 */
export function handleQueryError(
  error: unknown,
  options: {
    hookName?: string;
    endpoint?: string;
    method?: string;
    queryKey?: unknown;
    showToast?: boolean;
    customMessage?: string;
  } = {}
) {
  const {
    hookName,
    endpoint,
    method,
    queryKey,
    showToast = false, // Queries typically don't show toasts by default
    customMessage
  } = options;

  // Log to Sentry with context
  logErrorToSentry(error, {
    hookName,
    endpoint,
    method,
    variables: queryKey
  });

  // Show user-friendly error message if appropriate
  if (showToast && shouldShowErrorToUser(error)) {
    const message = customMessage || getErrorMessage(error);
    toast.error(message);
  }
}
